import { LucideIcon, Activity, Layers, Plug, UserX, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  QueueSummaryResponse,
  QueueSummarySection,
  QUEUE_ORDER,
} from "@/services/workerApi";
import { PluginStatus } from "@/services/pluginStatusApi";
import { WorkerAlert } from "../lib/alerts";

function StatCard({
  title,
  value,
  icon: Icon,
  hint,
  danger,
}: {
  title: string;
  value: string | number;
  icon: LucideIcon;
  hint?: string;
  danger?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={cn("h-4 w-4 text-muted-foreground", danger && "text-destructive")} />
      </CardHeader>
      <CardContent>
        <div className={cn("text-2xl font-bold", danger && "text-destructive")}>{value}</div>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

// Bandeau « santé flotte » : l'état global lisible en 3 secondes. Tout dérivé
// des données déjà chargées (summary + plugins), aucun appel supplémentaire.
export function KpiBand({
  summary,
  plugins,
  alerts,
}: {
  summary: QueueSummaryResponse | null;
  plugins: PluginStatus[] | null;
  alerts: WorkerAlert[];
}) {
  const inflight =
    ((summary?.enrichment as QueueSummarySection | undefined)?.processing_count ?? 0) +
    ((summary?.search as QueueSummarySection | undefined)?.processing_count ?? 0);

  const pending = summary
    ? QUEUE_ORDER.reduce(
        (acc, k) => acc + ((summary[k] as QueueSummarySection | undefined)?.count ?? 0),
        0,
      )
    : 0;

  const total = plugins?.length ?? 0;
  const anon = plugins?.filter((p) => p.is_anonymous).length ?? 0;
  const anonPct = total > 0 ? Math.round((anon / total) * 100) : 0;

  const fmt = (n: number) => n.toLocaleString("fr-FR");

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
      <StatCard
        title="En cours"
        value={fmt(inflight)}
        icon={Activity}
        hint="Candidats + recherche"
      />
      <StatCard title="En attente" value={fmt(pending)} icon={Layers} hint="Toutes files" />
      <StatCard
        title="Plugins connectés"
        value={fmt(total)}
        icon={Plug}
        hint={plugins ? "Heartbeat < 90s" : "—"}
      />
      <StatCard
        title="Flotte anonyme"
        value={`${anonPct}%`}
        icon={UserX}
        hint={total > 0 ? `${fmt(anon)} / ${fmt(total)} workers` : "—"}
      />
      <StatCard
        title="Alertes actives"
        value={fmt(alerts.length)}
        icon={AlertTriangle}
        danger={alerts.length > 0}
        hint={alerts.length > 0 ? "Voir le bandeau" : "Tout est nominal"}
      />
    </div>
  );
}
